/**
 * Editor AI Panel
 *
 * AI assistant sidebar for the flow editor.
 * Works with flows that have a content layer for AI-assisted editing.
 * Supports two edit modes:
 * - Content Mode: Edits semantic content (text, bullets, etc.) and recompiles
 * - Presentation Mode: Directly manipulates MMOs (size, position, color)
 *
 * Includes: undo support, validation warnings, rich feedback, edit history.
 */

import React, { useState, useCallback, useRef, useEffect, useContext } from 'react';

/** Elapsed seconds counter - ticks once per second while mounted */
function ElapsedSeconds({ className }: { className?: string }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return <span className={className || 'ai-elapsed-seconds'}>{seconds}s</span>;
}
import { MachineContext } from '../fsm/useMachine';
import { aiService } from './services/AIService';
import { useAIRecompile } from './hooks/useAIRecompile';
import { useAIPresentationEdit, detectEditMode as localDetectEditMode } from './hooks/useAIPresentationEdit';
import { MMOCommand } from './commands';
import { FlowWindow } from '../utils/globals';
import { resolveThemeFromHint } from '../../ai/styles/themeLibrary';
import { ImageGenerationProgress } from './hooks/useEditorImageGeneration';

declare let window: FlowWindow;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  showUndo?: boolean;
  warnings?: string[];
}

interface EditorAIPanelProps {
  isOpen: boolean;
  onClose: () => void;
  imageGenProgress?: ImageGenerationProgress;
  isGeneratingImages?: boolean;
}

/**
 * Get the content layer from the flow document
 * Checks multiple possible locations where the content layer may be stored
 */
function getContentLayer(): any {
  // Check window.flowboard first (may be set directly)
  if (window.flowboard?.content?.presentation) {
    return window.flowboard.content;
  }
  // Check window.flow_json.flowboard (the standard location from PHP)
  if (window.flow_json?.flowboard?.content?.presentation) {
    return window.flow_json.flowboard.content;
  }
  return null;
}

/**
 * Check if a flow has a content layer
 */
function hasContentLayer(): boolean {
  return !!getContentLayer();
}

/**
 * Editor AI Panel component
 */
export function EditorAIPanel({ isOpen, onClose, imageGenProgress, isGeneratingImages }: EditorAIPanelProps) {
  const [current, send] = useContext(MachineContext);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecompiling, setIsRecompiling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Hook for recompiling content changes into MMOs
  const { recompile, recompileWithTheme } = useAIRecompile();

  // Hook for direct presentation layer editing
  const {
    applyCommand,
    applyCommands,
    getScreenContext,
    getFormattedContext,
    getEditHistorySummary,
    parseLocally,
    classifyIntent,
    validateCommands: validateCmds,
    recordEdit,
    undo,
    canUndo,
    isAvailable: presentationEditAvailable,
  } = useAIPresentationEdit();

  // Get content layer from flow
  const contentLayer = getContentLayer();
  const hasContent = hasContentLayer();
  const currentScreenIndex = current.context.index;

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && hasContent) {
      inputRef.current?.focus();
    }
  }, [isOpen, hasContent]);

  // Initialize session from content layer if available
  useEffect(() => {
    if (hasContent && contentLayer?.sessionId) {
      setSessionId(contentLayer.sessionId);
    }
  }, [hasContent, contentLayer]);

  /**
   * Cancel the current request
   */
  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
      setIsRecompiling(false);
      // Update the last message to show cancelled
      setMessages((prev) => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg && lastMsg.role === 'assistant' && lastMsg.content.startsWith('::loading::')) {
          return [...prev.slice(0, -1), { ...lastMsg, content: 'Cancelled.' }];
        }
        return prev;
      });
    }
  }, []);

  /**
   * Handle undo action
   */
  const handleUndo = useCallback(() => {
    const result = undo();
    setMessages((prev) => [
      ...prev,
      {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: result.success ? result.message : result.message,
        timestamp: new Date(),
      },
    ]);
  }, [undo]);

  /**
   * Helper to update an assistant message by ID
   */
  const updateAssistantMessage = useCallback(
    (msgId: string, content: string, extra?: Partial<ChatMessage>) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === msgId ? { ...msg, content, ...extra } : msg
        )
      );
    },
    []
  );

  /**
   * Execute commands with validation and recording
   */
  const executeAndRecord = useCallback(
    (
      commands: MMOCommand[],
      userMessage: string,
      assistantMsgId: string
    ) => {
      // Validate first
      const validation = validateCmds(commands);
      const warningMessages = validation.warnings.map((w) => w.message);

      if (!validation.valid) {
        const errorMsg = validation.errors.map((e) => e.message).join('; ');
        updateAssistantMessage(assistantMsgId, `Cannot apply: ${errorMsg}`);
        return;
      }

      // Record snapshot before execution
      recordEdit(userMessage, validation.fixedCommands);

      // Execute
      const results = applyCommands(validation.fixedCommands);
      const allSucceeded = results.every((r) => r.success);
      const failedCount = results.filter((r) => !r.success).length;

      let feedbackContent: string;
      if (allSucceeded) {
        feedbackContent = results.length === 1 ? 'Done!' : `Done! Applied ${results.length} changes.`;
      } else if (failedCount < results.length) {
        feedbackContent = `Partially applied: ${results.length - failedCount} of ${results.length} changes succeeded.`;
      } else {
        const errorMsg = results.find((r) => r.error)?.error || 'Unknown error';
        feedbackContent = `Failed to apply changes: ${errorMsg}`;
      }

      // Log command execution failures to feedback system
      if (failedCount > 0) {
        const failedErrors = results
          .filter((r) => !r.success)
          .map((r) => r.error)
          .join('; ');
        aiService.logFeedback({
          request_text: userMessage,
          request_type: 'command_failure',
          error_detail: failedErrors,
        });
      }

      updateAssistantMessage(assistantMsgId, feedbackContent, {
        showUndo: allSucceeded || failedCount < results.length,
        warnings: warningMessages.length > 0 ? warningMessages : undefined,
      });
    },
    [validateCmds, recordEdit, applyCommands, updateAssistantMessage]
  );

  /**
   * Handle presentation layer edit (direct MMO manipulation)
   */
  const handlePresentationEdit = useCallback(
    async (message: string, assistantMsgId: string) => {
      try {
        // Check for undo request
        const intent = classifyIntent(message);
        console.log('[AI Panel] Intent:', intent.complexity, 'routeTo:', intent.routeTo, 'subIntents:', intent.subIntents.length, intent.subIntents.map(s => `${s.action}:${s.targetHint}`).join(', '));
        if (intent.isUndoRequest) {
          handleUndo();
          // Remove the processing message
          setMessages((prev) => prev.filter((msg) => msg.id !== assistantMsgId));
          return;
        }

        // Check for unsupported operations, give a helpful message instead of failing
        if (intent.isUnsupported) {
          updateAssistantMessage(
            assistantMsgId,
            intent.unsupportedReason || "I can't do that yet."
          );
          aiService.logFeedback({
            request_text: message,
            request_type: 'unsupported',
            unsupported_reason: intent.unsupportedReason,
          });
          return;
        }

        // Check for theme change requests
        if (intent.isThemeChangeRequest && intent.themeChangeHint) {
          const targetTheme = resolveThemeFromHint(intent.themeChangeHint);
          if (targetTheme) {
            updateAssistantMessage(assistantMsgId, '::loading::Changing theme...');
            const result = await recompileWithTheme(targetTheme.id);
            if (result.success) {
              updateAssistantMessage(assistantMsgId, `Changed theme to "${targetTheme.name}".`);
            } else {
              updateAssistantMessage(assistantMsgId, `Failed: ${result.error}`);
            }
          } else {
            updateAssistantMessage(
              assistantMsgId,
              `I couldn't find a "${intent.themeChangeHint}" theme. Try: dark, light, minimal, professional, corporate, creative, tech, neon, nature, midnight, startup.`
            );
          }
          return;
        }

        // Try local parsing first (instant)
        const localResult = parseLocally(message);
        console.log('[AI Panel] Local parse result:', localResult ? 'SUCCESS' : 'null (falling back to LLM)');
        if (localResult) {
          const commands = Array.isArray(localResult) ? localResult : [localResult];
          console.log('[AI Panel] Executing', commands.length, 'command(s):', commands.map(c => `${c.type}:${(c as any).operations?.map((o: any) => o.property).join(',') || c.type}`).join('; '));
          executeAndRecord(commands, message, assistantMsgId);
          return;
        }

        // Fall back to LLM for complex commands
        updateAssistantMessage(assistantMsgId, '::loading::Working');
        const screenContext = getScreenContext();

        // Pass rich context and edit history for better LLM results
        const richContext = getFormattedContext();
        const editHistory = getEditHistorySummary();

        const result = await aiService.getPresentationCommand(message, screenContext, {
          richContext,
          editHistory,
          routeTo: intent.routeTo,
          signal: abortControllerRef.current?.signal,
        });

        if (!result.success) {
          updateAssistantMessage(
            assistantMsgId,
            `Failed to process command: ${result.error}`
          );
          aiService.logFeedback({
            request_text: message,
            request_type: 'llm_failure',
            error_detail: result.error,
          });
          return;
        }

        // Apply command(s)
        let commands: MMOCommand[] = [];
        if (result.commands && result.commands.length > 0) {
          commands = result.commands;
        } else if (result.command) {
          commands = [result.command];
        } else {
          updateAssistantMessage(
            assistantMsgId,
            'I understood your request but couldn\'t generate a valid command.'
          );
          aiService.logFeedback({
            request_text: message,
            request_type: 'parse_failure',
            error_detail: 'No valid commands generated',
          });
          return;
        }

        executeAndRecord(commands, message, assistantMsgId);
      } catch (err) {
        updateAssistantMessage(
          assistantMsgId,
          `Error: ${err instanceof Error ? err.message : 'Unknown error'}`
        );
      }
    },
    [
      classifyIntent,
      handleUndo,
      recompileWithTheme,
      parseLocally,
      executeAndRecord,
      getScreenContext,
      getFormattedContext,
      getEditHistorySummary,
      updateAssistantMessage,
    ]
  );

  /**
   * Handle content layer edit (semantic content changes)
   */
  const handleContentEdit = useCallback(
    async (message: string, assistantMsgId: string) => {
      if (!sessionId) {
        updateAssistantMessage(
          assistantMsgId,
          'This flow doesn\'t have an AI session yet. To use content editing, create a new flow with AI at /create.'
        );
        return;
      }

      try {
        const result = await aiService.refine(sessionId, message, currentScreenIndex, abortControllerRef.current?.signal);

        if (result.success && result.presentation) {
          setIsRecompiling(true);
          updateAssistantMessage(assistantMsgId, '::loading::Applying changes');
          const recompileResult = await recompile(result.presentation);
          setIsRecompiling(false);

          if (recompileResult.success) {
            updateAssistantMessage(
              assistantMsgId,
              'Done! I\'ve updated the presentation.'
            );
          } else {
            updateAssistantMessage(
              assistantMsgId,
              `Content updated, but there was an issue refreshing: ${recompileResult.error}`
            );
          }
        } else {
          updateAssistantMessage(
            assistantMsgId,
            `Failed to update content: ${result.error || 'Unknown error'}`
          );
        }
      } catch (err) {
        setIsRecompiling(false);
        updateAssistantMessage(
          assistantMsgId,
          `Error: ${err instanceof Error ? err.message : 'Unknown error'}`
        );
      }
    },
    [sessionId, currentScreenIndex, recompile, updateAssistantMessage]
  );

  /**
   * Send a message to the AI
   */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!input.trim() || isLoading) return;

      const userMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: input.trim(),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInput('');
      setIsLoading(true);
      setError(null);

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Add processing message
      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: '::loading::Thinking',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      try {
        // Detect edit mode using local detection first (fast, no API call)
        const editModeResult = localDetectEditMode(userMessage.content);
        console.log('[AI Panel] Edit mode:', editModeResult.mode, 'confidence:', editModeResult.confidence, 'presentationAvailable:', presentationEditAvailable);

        if (editModeResult.mode === 'presentation' && presentationEditAvailable) {
          // Presentation layer edit: directly manipulate MMOs
          await handlePresentationEdit(userMessage.content, assistantMessage.id);
        } else if (sessionId) {
          // Content layer edit: use AI to modify semantic content
          await handleContentEdit(userMessage.content, assistantMessage.id);
        } else if (presentationEditAvailable) {
          // No session but presentation edit available - try presentation edit
          await handlePresentationEdit(userMessage.content, assistantMessage.id);
        } else {
          // No options available
          updateAssistantMessage(
            assistantMessage.id,
            'This flow doesn\'t have AI editing enabled. Create a new flow with AI at /create.'
          );
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setIsRecompiling(false);
      } finally {
        setIsLoading(false);
      }
    },
    [input, isLoading, sessionId, presentationEditAvailable, handlePresentationEdit, handleContentEdit, updateAssistantMessage]
  );

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  /**
   * Set input from suggestion
   */
  const handleSuggestion = useCallback((text: string) => {
    setInput(text);
    inputRef.current?.focus();
  }, []);

  if (!isOpen) return null;

  // Show limited state if no content layer but presentation edit is available
  if (!hasContent && !presentationEditAvailable) {
    return (
      <div className="editor-ai-panel">
        <div className="editor-ai-header">
          <h3>flowboardAI</h3>
          <button className="editor-ai-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>
        <div className="editor-ai-empty-state">
          <div className="editor-ai-empty-icon">
            <svg viewBox="0 0 24 24" width="48" height="48">
              <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <h4>AI Editing Not Available</h4>
          <p>This flow was created without AI content layer.</p>
          <p className="editor-ai-hint">
            To use AI-assisted editing, create a new flow at{' '}
            <a href="/create" target="_blank" rel="noopener noreferrer">
              /create
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Determine available modes
  const hasPresentationEdit = presentationEditAvailable;
  const hasContentEdit = hasContent && sessionId;

  const screenCount = current.context.flow.screens.length;

  return (
    <div className="editor-ai-panel">
      <div className="editor-ai-header">
        <h3>flowboardAI</h3>
        <span className="editor-ai-screen-indicator">
          Screen {currentScreenIndex + 1} of {screenCount}
        </span>
        <button className="editor-ai-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      </div>

      {/* Image generation progress banner */}
      {isGeneratingImages && imageGenProgress && !imageGenProgress.isComplete && (
        <div className="editor-ai-image-progress">
          <div className="editor-ai-image-progress-text">
            <span className="editor-ai-spinner" />
            <span>
              Generating images... {imageGenProgress.resolved} of {imageGenProgress.total} complete
              {imageGenProgress.errors > 0 && (
                <span className="editor-ai-image-progress-errors"> ({imageGenProgress.errors} failed)</span>
              )}
            </span>
          </div>
          <div className="editor-ai-image-progress-bar">
            <div
              className="editor-ai-image-progress-fill"
              style={{ width: `${((imageGenProgress.resolved + imageGenProgress.errors) / imageGenProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="editor-ai-messages">
        {messages.length === 0 && (
          <div className="editor-ai-welcome">
            <p>Ask me to make changes to your presentation.</p>
            {hasPresentationEdit && (
              <div className="editor-ai-mode-badge" title="Direct editing available">
                Direct Edit Mode
              </div>
            )}
            <div className="editor-ai-suggestions">
              {hasPresentationEdit && (
                <>
                  <button onClick={() => handleSuggestion('Make the title bigger')}>
                    Bigger title
                  </button>
                  <button onClick={() => handleSuggestion('Make it MUCH bigger')}>
                    Much bigger
                  </button>
                  <button onClick={() => handleSuggestion('Center all text')}>
                    Center all
                  </button>
                  <button onClick={() => handleSuggestion('Change the background to dark blue')}>
                    Dark background
                  </button>
                </>
              )}
              {hasContentEdit && (
                <>
                  <button onClick={() => handleSuggestion('Make the title more impactful')}>
                    Improve title
                  </button>
                  <button onClick={() => handleSuggestion('Add more bullet points to this slide')}>
                    Add bullets
                  </button>
                  <button onClick={() => handleSuggestion('Simplify the text on this slide')}>
                    Simplify
                  </button>
                </>
              )}
              {!hasPresentationEdit && !hasContentEdit && (
                <>
                  <button onClick={() => handleSuggestion('Make the title bigger')}>
                    Bigger title
                  </button>
                  <button onClick={() => handleSuggestion('Center the text')}>
                    Center text
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {messages.map((message) => {
          const isLoadingMsg = message.content.startsWith('::loading::');
          const loadingLabel = isLoadingMsg ? message.content.replace('::loading::', '') : '';
          return (
          <div
            key={message.id}
            className={`editor-ai-message editor-ai-${message.role}${isLoadingMsg ? ' editor-ai-loading' : ''}`}
          >
            <div className="editor-ai-content">
              {isLoadingMsg ? (
                <span className="editor-ai-status">
                  <span className="editor-ai-spinner" />
                  {loadingLabel}
                  <ElapsedSeconds className="ai-elapsed-inline" />
                </span>
              ) : (
                <>
                  {message.content}
                  {message.warnings && message.warnings.length > 0 && (
                    <div className="editor-ai-warnings">
                      {message.warnings.map((w, i) => (
                        <div key={i} className="editor-ai-warning">{w}</div>
                      ))}
                    </div>
                  )}
                  {message.showUndo && (
                    <button
                      className="editor-ai-undo-btn"
                      onClick={handleUndo}
                      title="Undo this change"
                    >
                      Undo
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="editor-ai-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="editor-ai-input-form">
        <div className="editor-ai-input-row">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask AI to edit..."
            rows={1}
            disabled={isLoading || isRecompiling}
          />
          {isLoading ? (
            <button
              type="button"
              onClick={handleCancel}
              className="editor-ai-cancel"
              aria-label="Cancel"
            >
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          ) : (
            <button
              type="submit"
              disabled={isRecompiling || !input.trim()}
              className="editor-ai-send"
              aria-label="Send"
            >
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94l18.04-8.01a.75.75 0 000-1.36L3.478 2.405z" />
              </svg>
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default EditorAIPanel;
